// src/components/common/MemberCard.jsx

import React, { useState, useEffect, useMemo } from "react";
import PhoneInput from "../../../../shared/PhoneInput";
import NameInput from "../../../../shared/NameInput";
import { FiEye, FiEyeOff, FiEdit2, FiCheck, FiUser } from "react-icons/fi";
import { useTheme } from "../../../../../contexts/ThemeContext";
import MemberEditModal from "./MemberEditModal";
import { 
  CARD_BASE, 
  INPUT_BASE, 
  SELECT_BASE, 
  LABEL_BASE,
  CARD_SPACING 
} from "../../../../../constants/styles";

const styles = {
  statusSection: {
    marginTop: "16px",
  },
  statusTitle: {
    fontSize: "14px",
    fontWeight: "medium",
    marginBottom: "12px",
  },
  statusBadge: {
    padding: "2px 8px",
    borderRadius: "9999px",
    fontSize: "12px",
    cursor: "pointer",
    backgroundColor: "#10B981",
    color: "#FFFFFF",
    display: "inline-block",
  },
  pipelineStatus: {
    display: "flex",
    alignItems: "center",
  },
  pipelineBadge: {
    padding: "2px 8px",
    borderRadius: "9999px",
    fontSize: "12px",
    cursor: "pointer",
    backgroundColor: "#10B981",
    color: "#FFFFFF",
    display: "inline-block",
  },
  loadingIndicator: {
    fontSize: "12px",
    fontStyle: "italic",
  },
  errorMessage: {
    fontSize: "12px",
  },
  tooltip: {
    position: "absolute",
    top: "-8px",
    right: "-8px",
    backgroundColor: "#1E40AF",
    color: "white",
    padding: "6px 12px",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "500",
    opacity: "0",
    transform: "translateY(4px)",
    transition: "all 0.2s ease-in-out",
    pointerEvents: "none",
    whiteSpace: "nowrap",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    zIndex: "50",
  },
  tooltipArrow: {
    position: "absolute",
    bottom: "-4px",
    right: "16px",
    width: "8px",
    height: "8px",
    backgroundColor: "#1E40AF",
    transform: "rotate(45deg)",
  }
};

// calculate age from ISO date string
const calculateAge = (dob) => {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// format US phone numbers
const formatPhoneNumber = (value) => {
  if (!value) return value;
  const digits = value.replace(/\D/g, "");
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

export default function MemberCard({
  data = {},
  onFieldChange,
  errors = {},
  prefixes = [],
  suffixes = [],
  genderTypes = [],
  householdId,
  pipelineData = { stages: [], currentStage: null },
  accentColor = "border-l-2 border-l-blue-600"
}) {
  const { isDarkMode } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // avatar initial
  const avatarInitial = (data.firstName?.[0] ?? "").toUpperCase();

  // determine gender label via memoized map
  const rawGenderId = data.genderTypeId ?? data.gender ?? "";
  const genderMap = useMemo(() => {
    return genderTypes.reduce((map, g) => {
      map[String(g.gender_type_id)] = g.gender_type_name;
      return map;
    }, {});
  }, [genderTypes]);
  const genderLabel = genderMap[String(rawGenderId)] || "—";
  
  // Determine gender-based colors
  const isFemale = genderLabel.toLowerCase() === 'female';
  const colors = {
    border: isFemale ? 'border-l-purple-600' : 'border-l-blue-600',
    borderHover: isFemale ? 'hover:border-purple-500 dark:hover:border-purple-400' : 'hover:border-blue-500 dark:hover:border-blue-400',
    avatar: isFemale ? 'bg-purple-700' : 'bg-[#131d80]',
    avatarHover: isFemale ? 'group-hover:bg-purple-600' : 'group-hover:bg-blue-600',
    tooltip: isFemale ? 'bg-purple-700' : 'bg-blue-700',
  };

  return (
    <>
      <div 
        className={`${CARD_BASE.replace('p-4', 'p-2 md:p-3')} border-l-2 ${colors.border} cursor-pointer group transition-all 
          hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-lg hover:scale-[1.02] relative
          ${colors.borderHover} duration-200 ease-in-out`} 
        onClick={() => setIsModalOpen(true)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Modern Tooltip */}
        <div 
          className={`absolute -top-2 -right-2 ${colors.tooltip} text-white px-3 py-1.5 rounded-lg text-xs font-medium 
            opacity-0 group-hover:opacity-100 transform group-hover:translate-y-0 translate-y-4 
            transition-all duration-200 ease-in-out pointer-events-none whitespace-nowrap shadow-lg z-50`}
        >
          <div className={`absolute -bottom-1 right-4 w-2 h-2 ${colors.tooltip} transform rotate-45`}></div>
          View/Edit Client Information
        </div>

        {/* Header - More compact */}
        <div className="flex items-center mb-1">
          <div className="flex items-center gap-x-2">
            <div className={`w-5 h-5 rounded-full ${colors.avatar} text-white flex items-center justify-center text-xs font-semibold
              ${colors.avatarHover} transition-colors duration-200`}>
              {avatarInitial}
            </div>
            <h3 className="text-xs md:text-sm font-medium text-gray-900 dark:text-white">
              {[data.prefix, data.firstName, data.middleName, data.lastName, data.suffix].filter(Boolean).join(" ")}
            </h3>
          </div>
        </div>

        <div className="space-y-1.5">
          {/* Demographics Grid - First Row */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <div>
              <label className="text-gray-500 dark:text-gray-400">Date of Birth</label>
              <p className="text-gray-900 dark:text-gray-100">{data.dateOfBirth || "—"}</p>
            </div>
            <div>
              <label className="text-gray-500 dark:text-gray-400">Age</label>
              <p className="text-gray-900 dark:text-gray-100">
                {data.dateOfBirth ? calculateAge(data.dateOfBirth) : "—"}
              </p>
            </div>
          </div>

          {/* Demographics Grid - Second Row */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <div>
              <label className="text-gray-500 dark:text-gray-400">Gender</label>
              <p className="text-gray-900 dark:text-gray-100">{genderLabel}</p>
            </div>
            <div>
              <label className="text-gray-500 dark:text-gray-400">SSN</label>
              <p className="text-gray-900 dark:text-gray-100">•••••••••</p>
            </div>
          </div>

          {/* Contact Grid - Emails */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            {/* Primary Email */}
            <div>
              <label className="text-gray-500 dark:text-gray-400">Email</label>
              <p className="text-gray-900 dark:text-gray-100">{data.email || "—"}</p>
            </div>
            {/* Alternate Email */}
            <div>
              <label className="text-gray-500 dark:text-gray-400">Alternate Email</label>
              <p className="text-gray-900 dark:text-gray-100">{data.alternateEmail || "—"}</p>
            </div>
          </div>

          {/* Contact Grid - Phones */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            {/* Primary Phone */}
            <div>
              <label className="text-gray-500 dark:text-gray-400">Phone</label>
              <p className="text-gray-900 dark:text-gray-100">
                {data.phoneNumber ? formatPhoneNumber(data.phoneNumber) : "—"}
              </p>
            </div>
            {/* Alternate Phone */}
            <div>
              <label className="text-gray-500 dark:text-gray-400">Alternate Phone</label>
              <p className="text-gray-900 dark:text-gray-100">
                {data.alternatePhone ? formatPhoneNumber(data.alternatePhone) : "—"}
              </p>
            </div>
          </div>

          {/* Pipeline Status - Only show if data exists */}
          {pipelineData.currentStage && (
            <div className="grid grid-cols-2 gap-x-4 text-xs">
              <div>
                <label className="text-gray-500 dark:text-gray-400 block">Stage</label>
                <div className="inline-block px-1.5 py-0.5 text-[10px] rounded-full bg-green-500 text-white mt-0.5">
                  {pipelineData.currentStage?.stage_name || "N/A"}
                </div>
              </div>
              <div>
                <label className="text-gray-500 dark:text-gray-400 block">Status</label>
                <div className="inline-block px-1.5 py-0.5 text-[10px] rounded-full bg-green-500 text-white mt-0.5">
                  {pipelineData.currentStage?.status_name || "N/A"}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <MemberEditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        data={data}
        onFieldChange={onFieldChange}
        errors={errors}
        prefixes={prefixes}
        suffixes={suffixes}
        genderTypes={genderTypes}
        pipelineData={pipelineData}
      />
    </>
  );
}
